import { FC, useEffect, useRef, useState } from 'react';
import { RaceDatas } from '../types';
import './distances.scss';
import { Nump } from './nump';
import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons';

/* -------------------------------------------------------------------------------- *\
|                               Constantes
\* -------------------------------------------------------------------------------- */
const c_fontsize_label: number = 16;
const yaxismargin = 5;
const yaxismargin2 = 3;
const textmargin = 16;
const showlength = 45;
var g_test: number = 0;
/* -------------------------------------------------------------------------------- *\
|                               DistanceDatas
\* -------------------------------------------------------------------------------- */
class DistanceDatas
{
    public number: number = 0;
    public rank: number = 0;
    public eprm: number = 0;
    public delta: number = 0;
    public leftarrow: string = "transparent";
    public rightarrow: string = "transparent";
}
/* -------------------------------------------------------------------------------- *\
|                               DistancesProps
\* -------------------------------------------------------------------------------- */
interface DistancesProps
{
	race: RaceDatas,
	children?: Node
}
/* -------------------------------------------------------------------------------- *\
|                               distance
\* -------------------------------------------------------------------------------- */
function distancetostring(distance: number): string
{
    if (distance === 0) return "";

    return distance.toFixed(1) + "m";
}
/* -------------------------------------------------------------------------------- *\
|                               Distances
\* -------------------------------------------------------------------------------- */
var olddistances: DistanceDatas[] = [];
export const Distances : FC<DistancesProps> = ({race}) =>
{
    const [distances, setDistances] = useState<DistanceDatas[]>([]);

    const refcontainer = useRef<HTMLDivElement>(null);
    const refcanvas = useRef<HTMLCanvasElement>(null);
    const [distance, setDistance] = useState<number>(0);
    const [width, setWidth] = useState<number>(300);

    /* -------------------------------------------------------------------------------- *\
    |                               drawGraph
    \* -------------------------------------------------------------------------------- */
    function drawGraph(ctx: CanvasRenderingContext2D)
    {
        g_test++;
        if (g_test < 3)
        {
            return;
        }
        g_test = 0;

        var xpcdep = 0;
        var xpcarr = width;
        const height = 30;

        var axisy = 5;

        ctx.fillStyle = "black";
        ctx.beginPath();
        ctx.rect(0, 0, width, height);
        ctx.fill();

        const finished = race.positions.find(n => n.eprmarr > 0) !== undefined;

        var dpdpmax = finished ? distance : 0;
        if (!finished)
        {
            for (const position of race.positions)
            {
                if (position.dpdp > dpdpmax) dpdpmax = position.dpdp;
            }
        }
        ctx.strokeStyle = "white";
        ctx.lineWidth = 1;

        var drawstart: number = dpdpmax - showlength;
        var drawend: number = dpdpmax;
        const delta: number = 25;

        ctx.beginPath();
        ctx.moveTo(xpcdep, axisy);
        ctx.lineTo(xpcarr, axisy);
        ctx.stroke();

        ctx.fillStyle = "white";
        ctx.textAlign = "center";
        ctx.font = `${c_fontsize_label}px -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif`;

        // Affichage des distances sous l'axe des xx
        if (dpdpmax === 0)
        {
            ctx.textAlign = "right";
            ctx.fillText("Start", xpcarr, axisy + yaxismargin + textmargin);
        }
        else if (finished)
        {
            ctx.textAlign = "right";
            ctx.fillText("Finish", xpcarr, axisy + yaxismargin + textmargin);
        }
        ctx.textAlign = "center";

        var lastx = xpcarr;
        for (var d = 0; d < distance; d += delta)
        {
            var xt: number = (distance - d);
            xt = (xt - drawstart) / (drawend - drawstart);
            xt *=  (xpcarr - xpcdep);
            xt += xpcdep;

            if (xt >= xpcdep && xt <= xpcarr)
            {
                ctx.beginPath();
                ctx.moveTo(xt, axisy - yaxismargin2);
                ctx.lineTo(xt, axisy + yaxismargin2);
                ctx.stroke();
                if (lastx - xt > 30)
                {
                    ctx.fillText(d.toString(), xt, axisy + yaxismargin + textmargin);
                    lastx = xt;
                }
            }
        }
    }

    useEffect(() =>
    {
        if (refcontainer && refcontainer.current && width !== refcontainer.current.clientWidth)
        {
            setWidth(refcontainer.current.clientWidth);
        }
        setDistance(race.distance);

        const finished = race.positions.find(n => n.eprmarr > 0) !== undefined;
        const newdistances: DistanceDatas[] = race.positions.map((position) =>
        {
            const distance: DistanceDatas = new DistanceDatas();            
            distance.number = position.number;            
            distance.rank = finished ? position.rankarr : position.rank;
            distance.eprm = finished ? position.eprmarr : position.eprm;

            const f = olddistances.find(x => x.number === distance.number);
            if (f)
            {
                distance.delta = 3.6 * 10 * (f.eprm - distance.eprm);
                const ad = Math.abs(distance.delta);
                var color: string = ad < 3 ? "transparent" : (distance.delta < 0 ? "red" : "green");
                if (distance.delta < 0)
                {
                    distance.leftarrow = color;
                    distance.rightarrow = "transparent";
                }
                else
                {
                    distance.leftarrow = "transparent";
                    distance.rightarrow = color;
                }
            }
            return distance;
        }).sort((a, b) => a.number - b.number);
        setDistances(newdistances);
        olddistances = newdistances;
        // eslint-disable-next-line
    }, [race]);

    useEffect(() =>
    {
        const canvas = refcanvas.current;
        if (canvas)
        {
            const context = canvas.getContext('2d');
            if (context)
            {
                drawGraph(context);
            }
        }
        // eslint-disable-next-line
    }, [race, width]);

    return (
        <div className='distances'>
            <div className='distances__main'>
                {
                    distances.map((distance, distance_index) => 
                    {
                        if (distance.rank === 0)
                        {
                            return (
                                <div className='distances__main__line' key={'section_' + distance_index}>
                                    <div className='distances__main__line__comp'>
                                        <Nump number={distance.number} size={24} />
                                        <div className='distances__main__line__nocomp'>Not in race</div>
                                    </div>
                                </div>
                            )
                        }
                        const maxdistance: number = showlength;
                        const right: string = 'calc(' + Math.min(distance.eprm, maxdistance) + '/' + maxdistance + ' * (100% - 82px))';
                        return (
                            <div className='distances__main__line' key={'section_' + distance_index}>
                                <div className='distances__main__line__comp' style={{right}}>
                                    <div className='distances__main__line__eprm'>{distancetostring(distance.eprm)}</div>
                                    <div className='distances__main__line__rank'>{'(' + distance.rank + ')'}</div>
                                    <CaretLeftOutlined className='distances__main__line__arrowleft' style={{color: distance.leftarrow}}></CaretLeftOutlined>
                                    <Nump number={distance.number} size={24} />
                                    <CaretRightOutlined className='distances__main__line__arrowright' style={{color: distance.rightarrow}}></CaretRightOutlined>
                                </div>
                            </div>
                        )
                    })
                }
                <div ref={refcontainer} className='distances__main__axis'>
                    <canvas ref={refcanvas} width={width} height={30} onContextMenu={e => e.preventDefault()}/>
                </div>
            </div>
        </div>
    );
}
